/**
 * The MediaStreamTrack ened event does not fire by design
 * when stop() is called, only when the track is ended.
 * This function monkey-patches the stop()
 * method to call the callback when the track is either
 * stopped or ended.
 *
 * Add a listener to a MediaStreamTrack that will be called when
 * the track is stopped or ended.
 * This is useful to cleanup resources when the track is stopped.
 * @param track - The MediaStreamTrack to add the listener to.
 * @param callback - The callback to call when the track is stopped.
 * @returns A function to remove the listener.
 */
// Store callbacks per track (WeakMap for automatic garbage collection)
const trackStopCallbacks = new WeakMap();
export function addMediaStreamTrackStopListener(track, callback) {
    // Get or create callback set for this track
    let data = trackStopCallbacks.get(track);
    if (!data) {
        // Store original stop method
        const originalStop = track.stop.bind(track);
        // Create ended handler
        const endedHandler = () => {
            const trackData = trackStopCallbacks.get(track);
            if (trackData) {
                trackData.callbacks.forEach((cb) => cb());
            }
        };
        // Create data structure
        data = {
            callbacks: new Set(),
            originalStop,
            endedHandler,
        };
        trackStopCallbacks.set(track, data);
        // Monkey-patch stop() only once per track
        track.stop = () => {
            const trackData = trackStopCallbacks.get(track);
            if (trackData) {
                // Call all registered callbacks
                trackData.callbacks.forEach((cb) => cb());
            }
            // Call original stop
            originalStop();
        };
        // Listen for natural 'ended' event
        track.addEventListener('ended', endedHandler);
    }
    // Add this callback
    data.callbacks.add(callback);
    // Return cleanup function
    return () => {
        const trackData = trackStopCallbacks.get(track);
        if (!trackData)
            return;
        // Remove this callback
        trackData.callbacks.delete(callback);
        // If no more callbacks, restore original behavior
        if (trackData.callbacks.size === 0) {
            // Restore original stop method
            track.stop = trackData.originalStop;
            // Remove ended event listener
            track.removeEventListener('ended', trackData.endedHandler);
            // Remove from WeakMap
            trackStopCallbacks.delete(track);
        }
    };
}
